import type { ChangeEvent } from 'react';
import { useState } from 'react';

import { useMountEffect } from '@react-hookz/web';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import { ToolTip } from 'src/app/components/Elements/ToolTip';
import { UploadIDInputUnwired } from 'src/app/components/Form/UploadIDField';
import { loadAccount, segmentTrack, setAccountImageID } from 'src/app/state/actions';
import { bffFetchCart } from 'src/app/state/actions/cart';
import { pushNextCheckoutState } from 'src/app/state/actions/checkout';
import { getAccountIDVerificationStatus, getBFFCart, getIsBffCartFetching } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import { reduceImageSize } from 'src/app/utils/reduceImageSize';
import { IMAGE_UPLOAD_QUALITY, IMAGE_UPLOAD_SIZE, IMAGE_UPLOAD_WIDTH, IS_MOBILE } from 'src/consts';

import styles from './styles.module.scss';

const Renderer = ({ cartId }) => {
  const [isSubmitting, setSubmitting] = useState(false);

  const bffCart = useSelector(getBFFCart);
  const bffCartIsFetching = useSelector(getIsBffCartFetching);
  const idVerificationStatus = useSelector(getAccountIDVerificationStatus);

  const dispatch = useDispatch();
  const dispatchLoadCart = cartId => dispatch(bffFetchCart(cartId));
  const dispatchPushNextCheckoutState = async () => {
    await dispatch(pushNextCheckoutState());
  };
  const dispatchSetAccountImageID = async file => {
    await dispatch(setAccountImageID(file));
  };
  const dispatchSegmentTrack = (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload));

  useMountEffect(() => {
    dispatchSegmentTrack(UNIFIED.viewAddPhotoId, { source: 'Checkout' });
    if (!bffCart && !bffCartIsFetching) {
      dispatchLoadCart(cartId);
    }
  });

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    setSubmitting(true);
    const file = event.target.files?.[0] ?? null;
    if (!file) throw new Error('No file');
    reduceImageSize(
      file,
      IMAGE_UPLOAD_SIZE,
      IMAGE_UPLOAD_WIDTH,
      Infinity,
      IMAGE_UPLOAD_QUALITY,
      async function (blob: Blob | null) {
        try {
          await dispatchSetAccountImageID(blob);
          // Need to load the account after uploading the id for verfification as the
          // id verification status is on the account model and we need the updated value
          await dispatch(loadAccount());
          await dispatchPushNextCheckoutState();
          dispatchSegmentTrack(UNIFIED.submitPhotoId, { source: 'Checkout' });
        } catch (error) {
          console.error('handleFileUpload issue:', { error });
          setSubmitting(false);
        }
      }
    );
  };

  if (bffCartIsFetching || isSubmitting) {
    return (
      <StandardPage title={'Add Photo ID'}>
        <Spinner centered />
      </StandardPage>
    );
  } else {
    return (
      <StandardPage title={'Add Photo ID'}>
        <div className={styles.component}>
          <div>
            {!IS_MOBILE && (
              <p className={styles.spaceBottom}>
                For certain medications, we need an image of your photo ID to verify your identity. Please upload a
                photo of your ID below, or use the Capsule app to add a photo straight from your mobile phone.{' '}
              </p>
            )}
            {IS_MOBILE && (
              <p className={styles.spaceBottom}>
                For certain medications, we need an image of your photo ID to verify your identity.{' '}
              </p>
            )}
            <UploadIDInputUnwired
              idVerificationStatus={idVerificationStatus}
              name="photoUpload"
              handleFileUpload={handleFileUpload}
            />
          </div>
          <div className="mt-4 d-flex justify-content-center">
            <ToolTip
              anchorText="What forms of ID can I use?"
              header="What forms of ID can I use?"
              body="Any government issued photo ID – including passport, drivers license, state ID, or school ID."
            />
          </div>
        </div>
      </StandardPage>
    );
  }
};

export default Renderer;
